import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("loading"); // loading, success, already_verified, error
  const [error, setError] = useState("");

  // Redirect logged-in users to profile if no token (they accessed /verify-email directly)
  useEffect(() => {
    if (!token) {
      const authToken = localStorage.getItem("token");
      const user = localStorage.getItem("user");
      if (authToken && user) {
        try {
          const userData = JSON.parse(user);
          if (userData.role === "admin" || userData.role === "manager") {
            navigate("/admin", { replace: true });
          } else {
            navigate("/app/profile", { replace: true });
          }
        } catch (e) {
          // Invalid user data
        }
      }
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("No verification token provided");
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch(`${API}/public/auth/verify/${token}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Verification failed");
      }

      if (data.already_verified) {
        setStatus("already_verified");
      } else {
        setStatus("success");
      }
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 max-w-md w-full text-center border border-white/20"
      >
        {/* Loading State */}
        {status === "loading" && (
          <>
            <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Verifying Email</h2>
            <p className="text-gray-400">Please wait while we verify your email address...</p>
          </>
        )}

        {/* Success State */}
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Email Verified!</h2>
            <p className="text-gray-300 mb-6">
              Your email has been verified successfully. You can now log in to your account.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold px-6 py-3 rounded-lg hover:from-cyan-400 hover:to-purple-500 transition-all"
              data-testid="verify-login-button"
            >
              Continue to Login
              <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {/* Already Verified State */}
        {status === "already_verified" && (
          <>
            <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-cyan-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Already Verified</h2>
            <p className="text-gray-300 mb-6">
              This email has already been verified. You can log in to your account.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold px-6 py-3 rounded-lg hover:from-cyan-400 hover:to-purple-500 transition-all"
            >
              Go to Login
              <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {/* Error State */}
        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-gray-300 mb-6">{error}</p>
            <div className="space-y-3">
              <Link
                to="/register"
                className="block w-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold px-6 py-3 rounded-lg hover:from-cyan-400 hover:to-purple-500 transition-all"
              >
                Try Registering Again
              </Link>
              <Link
                to="/login"
                className="block text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Or go to Login
              </Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
